import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import type { MapPoint } from '@/lib/maps/types';

export type MapOverlayProps = {
  /** Where on the earth this box sits. Its top-left is placed at the projected pixel. */
  point: MapPoint;
  children: ReactNode;
};

/**
 * Anchor arbitrary HTML to a geographic point on a `<MapCanvas>`.
 *
 * The **second** module allowed to import `@vis.gl/react-google-maps`, beside the canvas itself — a
 * per-file exemption in `eslint.config.js`, added deliberately (ADR-0017). It exists because a
 * Device marker (and later the cluster badge and both anchored panels) is project-styled HTML, not
 * a Google marker: `OverlayView` + `createPortal` needs no Cloud Map ID, where `AdvancedMarker`
 * would.
 *
 * Two details are load-bearing:
 *
 * - **The box's position comes only from `point`.** There is no snapping, no centroid and no
 *   nearest-road correction anywhere in the path (113587 §1c).
 * - **It culls itself.** `draw()` is called by the SDK on every camera change, so asking the map
 *   whether its bounds still contain the point delivers "elements are drawn and removed as the
 *   Grower pans and zooms" (113586 §1e) with no camera subscription and no bounds arithmetic of our
 *   own. It fails **open**: a map that cannot yet report bounds draws the overlay rather than
 *   hiding the whole layer.
 *
 * The container takes no pointer events; an interactive child opts itself back in.
 */
export function MapOverlay({ point, children }: MapOverlayProps) {
  const map = useMap();
  const [container] = useState(() => {
    const el = document.createElement('div');
    el.style.position = 'absolute';
    el.style.pointerEvents = 'none';
    return el;
  });
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!map || typeof google === 'undefined') return undefined;

    const overlay = new google.maps.OverlayView();
    overlay.onAdd = () => {
      overlay.getPanes()?.floatPane.appendChild(container);
    };
    overlay.draw = () => {
      const latLng = new google.maps.LatLng(point.lat, point.lon);
      const pixel = overlay.getProjection()?.fromLatLngToDivPixel(latLng);
      if (pixel) {
        container.style.left = `${pixel.x}px`;
        container.style.top = `${pixel.y}px`;
      }
      setInView(map.getBounds()?.contains(latLng) ?? true);
    };
    overlay.onRemove = () => {
      container.parentNode?.removeChild(container);
    };
    overlay.setMap(map);

    return () => overlay.setMap(null);
  }, [map, point.lat, point.lon, container]);

  return inView ? createPortal(children, container) : null;
}
